import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, ChevronDown, ChevronRight, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import IndexWorkspaceShell from '@/components/index/IndexWorkspaceShell';
import { methodologyPhaseLabel } from '@/config/methodologyAnalysisPipeline';
import { formatScriptId } from '@/utils/formatIndexId';
import {
  getSessionSteps,
  type StepReviewItem,
  type StepReviewLevel,
} from '@/services/scriptService';
import { getScorecardById } from '@/services/scorecardService';
import { markGenerateScorecardComplete } from '@/services/workflowService';

function levelClasses(level: StepReviewLevel): string {
  return cn(
    'inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wide',
    level === 'ok' && 'border-green-500/50 bg-green-950/50 text-green-300',
    level === 'fallthrough' && 'border-amber-500/50 bg-amber-950/40 text-amber-200',
    level === 'failed' && 'border-red-500/50 bg-red-950/50 text-red-300',
  );
}

function stepTitle(step: StepReviewItem): string {
  const key = step.strand_id ? `${step.strand_id}:${step.step_id}` : step.step_id;
  return methodologyPhaseLabel(key) || step.step_id;
}

function StepRow({
  step,
  open,
  onToggle,
}: {
  step: StepReviewItem;
  open: boolean;
  onToggle: () => void;
}) {
  const outputJson = useMemo(() => {
    try {
      return JSON.stringify(step.output ?? {}, null, 2);
    } catch {
      return String(step.output ?? {});
    }
  }, [step.output]);

  return (
    <section className="rounded-lg border border-[#2a2a2a] bg-[#0f0f0f] overflow-hidden">
      <button
        type="button"
        className="flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-white/[0.02]"
        onClick={onToggle}
        aria-expanded={open}
      >
        {open ? (
          <ChevronDown className="mt-0.5 h-4 w-4 shrink-0 text-white/40" />
        ) : (
          <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-white/40" />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-sm text-white">{step.step_id}</span>
            <span className={levelClasses(step.level)}>{step.label}</span>
            {step.executor_kind && (
              <span className="font-mono text-[10px] uppercase text-white/35">
                {step.executor_kind}
                {step.executor_id ? ` · ${step.executor_id}` : ''}
              </span>
            )}
          </div>
          <p className="mt-1 text-sm font-sans text-white/65 truncate">{stepTitle(step)}</p>
          {step.level !== 'ok' && step.detail && step.detail !== 'ok' && (
            <p className="mt-1 text-xs font-mono text-white/45 line-clamp-2">{step.detail}</p>
          )}
        </div>
      </button>
      {open && (
        <div className="border-t border-[#2a2a2a] px-4 py-3 space-y-3">
          {(step.fallthrough_errors?.length || step.error) && (
            <div className="rounded border border-amber-500/20 bg-amber-950/20 px-3 py-2 text-xs font-mono text-amber-100/80 whitespace-pre-wrap">
              {step.error || step.fallthrough_errors?.join('\n')}
            </div>
          )}
          <pre className="max-h-[480px] overflow-auto ni-scroll rounded border border-[#1f1f1f] bg-black/60 p-3 text-[11px] leading-relaxed font-mono text-white/75 whitespace-pre-wrap break-words">
            {outputJson}
          </pre>
        </div>
      )}
    </section>
  );
}

const AnalysisStepReview: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [steps, setSteps] = useState<StepReviewItem[]>([]);
  const [scriptTitle, setScriptTitle] = useState<string | null>(null);
  const [scriptId, setScriptId] = useState<string | null>(null);
  const [counts, setCounts] = useState({ ok: 0, fallthrough: 0, failed: 0 });
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [filter, setFilter] = useState<'all' | StepReviewLevel>('all');
  const [continuing, setContinuing] = useState(false);

  const load = useCallback(async () => {
    if (!sessionId) {
      setError('Session ID is required');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await getSessionSteps(sessionId, { includeOutput: true });
      setSteps(data.steps ?? []);
      setScriptTitle(data.script_title ?? null);
      setScriptId(data.script_id ?? sessionId);
      setCounts({
        ok: data.ok_count ?? 0,
        fallthrough: data.fallthrough_count ?? 0,
        failed: data.failed_count ?? 0,
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load step outputs');
      setSteps([]);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    if (filter === 'all') return steps;
    return steps.filter((s) => s.level === filter);
  }, [steps, filter]);

  const continueToScorecard = async () => {
    if (!sessionId) return;
    setContinuing(true);
    setError(null);
    try {
      // Methodology analyse persists step JSON only; do not mark Generate Scorecard
      // complete or open /analysis until a real scorecard document exists.
      await getScorecardById(sessionId);
      try {
        await markGenerateScorecardComplete(sessionId);
      } catch {
        /* navigation still proceeds once the scorecard is readable */
      }
      navigate(`/analysis/${sessionId}`);
    } catch {
      setError(
        'Scorecard not assembled yet for this methodology run. Step outputs are ready — ' +
          'Generate Scorecard stays unavailable until the ratings / scorecard assembler is wired.',
      );
    } finally {
      setContinuing(false);
    }
  };

  const rowKey = (step: StepReviewItem, idx: number) =>
    `${step.strand_id || 'main'}::${step.step_id}::${idx}`;

  return (
    <IndexWorkspaceShell activeNav="audit">
      <div className="mx-auto max-w-5xl">
        <header className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <button
              type="button"
              onClick={() => navigate('/workflow')}
              className="mb-3 inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-white/40 hover:text-white/70"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Workflow
            </button>
            <h1 className="font-sans text-2xl text-white tracking-tight">Step output review</h1>
            <p className="mt-2 max-w-2xl text-sm font-sans text-white/50">
              Review methodology step accuracy before generating the Nuclius Index scorecard.
            </p>
            {(scriptTitle || scriptId || sessionId) && (
              <p className="mt-3 text-[11px] font-mono text-white/35">
                {scriptId && (
                  <>
                    Script · <span className="text-white/60">{formatScriptId(scriptId)}</span>
                  </>
                )}
                {scriptTitle && <span className="text-white/30"> · {scriptTitle}</span>}
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-2 shrink-0">
            <Button variant="indexOutline" onClick={() => void load()} disabled={loading}>
              Refresh
            </Button>
            <Button
              variant="indexPrimary"
              onClick={() => void continueToScorecard()}
              disabled={!sessionId || continuing || loading}
              title="Opens the Nuclius Index report only when a scorecard document exists"
            >
              {continuing ? 'Checking…' : 'Continue to scorecard'}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </header>

        <div className="mb-6 flex flex-wrap items-center gap-3 text-xs font-mono">
          <button
            type="button"
            className={cn(
              'rounded border px-2 py-1',
              filter === 'all' ? 'border-white/40 text-white' : 'border-[#2a2a2a] text-white/45',
            )}
            onClick={() => setFilter('all')}
          >
            All {steps.length}
          </button>
          <button
            type="button"
            className={cn(
              'rounded border px-2 py-1',
              filter === 'ok' ? 'border-green-500/60 text-green-300' : 'border-[#2a2a2a] text-white/45',
            )}
            onClick={() => setFilter('ok')}
          >
            Ok {counts.ok}
          </button>
          <button
            type="button"
            className={cn(
              'rounded border px-2 py-1',
              filter === 'fallthrough'
                ? 'border-amber-500/60 text-amber-200'
                : 'border-[#2a2a2a] text-white/45',
            )}
            onClick={() => setFilter('fallthrough')}
          >
            Fall-through {counts.fallthrough}
          </button>
          <button
            type="button"
            className={cn(
              'rounded border px-2 py-1',
              filter === 'failed' ? 'border-red-500/60 text-red-300' : 'border-[#2a2a2a] text-white/45',
            )}
            onClick={() => setFilter('failed')}
          >
            Failed {counts.failed}
          </button>
          <Button
            variant="ghost"
            size="sm"
            className="text-white/40 hover:text-white/70"
            onClick={() => {
              const next: Record<string, boolean> = {};
              filtered.forEach((s, i) => {
                next[rowKey(s, i)] = true;
              });
              setExpanded(next);
            }}
          >
            Expand filtered
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-white/40 hover:text-white/70"
            onClick={() => setExpanded({})}
          >
            Collapse
          </Button>
        </div>

        {error && (
          <div className="mb-6 flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-200 font-sans">
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {loading ? (
          <div className="flex items-center gap-2 py-16 text-white/50 font-sans text-sm">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading step outputs…
          </div>
        ) : filtered.length === 0 ? (
          <p className="py-16 text-center text-sm text-white/45 font-sans">
            {steps.length === 0
              ? 'No methodology step results found for this session yet.'
              : 'No steps match this filter.'}
          </p>
        ) : (
          <div className="space-y-2">
            {filtered.map((step, idx) => {
              const key = rowKey(step, idx);
              return (
                <StepRow
                  key={key}
                  step={step}
                  open={!!expanded[key]}
                  onToggle={() =>
                    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }))
                  }
                />
              );
            })}
          </div>
        )}

        <footer className="mt-10 border-t border-[#2a2a2a] pt-6 flex justify-end">
          <Button
            variant="indexPrimary"
            onClick={() => void continueToScorecard()}
            disabled={!sessionId || continuing || loading}
          >
            Continue to scorecard
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </footer>
      </div>
    </IndexWorkspaceShell>
  );
};

export default AnalysisStepReview;
